import React, { useEffect } from 'react';
import { useBranding } from '../../hooks/useBranding';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { login } from '../../store/actions/user.actions';
import { clearError } from '../../store/reducers/user.reducer';
import { setRememberDevice, getRememberDevice } from '../../hooks/useInactivityLogout';
import '../../glass.css';

interface LoginFormProps {
  onSwitchToSignUp: () => void;
  onSwitchToForgotPassword: () => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignUp, onSwitchToForgotPassword }) => {
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.user);
  const [showPassword, setShowPassword] = React.useState(false);
  const [rememberDevice, setRemember] = React.useState(getRememberDevice);
  const [showRemember, setShowRemember] = React.useState(false);
  const branding = useBranding();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>();

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      await dispatch(login(data));
      reset();
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
    <div className="glassContainer auth-form-container" style={{ padding: '20px', maxWidth: '500px', maxHeight: '90vh', background:'radial-gradient(circle at center, color-mix(in srgb, var(--primary-color, #3a9397) 20%, transparent), rgba(0, 0, 0, 0.63) 70%)', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', overflowY: 'auto' }}>
     
        {branding.showBlockLogin && (
          <div className="logobox glassBtn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
            <img
              src={branding.logoUrl || '/logo192.png'}
              alt="Logo"
              className="logo"
              style={{ height: 40, marginRight: 8 }}
            />
            <div style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'inherit' }}>
              <span style={{ color: 'var(--primary-color, #3a9397)' }}>{branding.part1}</span>
              <span style={{ color: 'var(--primary-light, #ff9800)' }}>{branding.part2}</span>
            </div>
          </div>
        )}


        {branding.loginBannerEnabled && branding.loginBannerText && (
          <Alert severity="info" sx={{ mb: 2, color: 'var(--text-primary)', backgroundColor: 'var(--auth-field-background)' }}>
            {branding.loginBannerText}
          </Alert>
        )}

        {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          autoFocus
          label="Email"
          type="email"
          {...register('email', {
          required: 'Email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address',
          },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          sx={{
            mb: 2,
            '& .MuiInputBase-root': {
              backgroundColor: 'var(--auth-field-background) !important',
            },
            '& .MuiInputBase-root:hover': {
              backgroundColor: 'var(--auth-field-background) !important',
            },
            '& .MuiInputBase-root.Mui-focused': {
              backgroundColor: 'var(--auth-field-background) !important',
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent !important',
            },
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
              WebkitBoxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
              boxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
              WebkitTextFillColor: 'var(--text-primary) !important',
              transition: 'background-color 5000s ease-in-out 0s',
            }
          }}
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          {...register('password', {
          required: 'Password is required',
          minLength: {
            value: 6,
            message: 'Password must be at least 6 characters',
          },
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
          sx={{
            mb: 3,
            '& .MuiInputBase-root': {
              backgroundColor: 'var(--auth-field-background) !important',
            },
            '& .MuiInputBase-root:hover': {
              backgroundColor: 'var(--auth-field-background) !important',
            },
            '& .MuiInputBase-root.Mui-focused': {
              backgroundColor: 'var(--auth-field-background) !important',
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent !important',
            },
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
              WebkitBoxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
              boxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
              WebkitTextFillColor: 'var(--text-primary) !important',
              transition: 'background-color 5000s ease-in-out 0s',
            }
          }}
          disabled={loading}
          InputProps={{
          endAdornment: (
            <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleTogglePasswordVisibility}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
            </InputAdornment>
          ),
          }}
        />

        {showRemember && (
          <FormControlLabel
            sx={{ mb: 1, color: 'text.secondary', fontSize: '13px' }}
            control={
              <Checkbox
                size="small"
                checked={rememberDevice}
                onChange={(e) => {
                  setRemember(e.target.checked);
                  setRememberDevice(e.target.checked);
                }}
                sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' } }}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Remember this device</Typography>}
          />
        )}

        <Button
          type="submit"
          className='glassBtn'
          fullWidth
          // variant="contained"
          disabled={loading}
          sx={{ mb: 1 }}
        >

          {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
        </Button>
       
        </Box>

        {branding.showAuthLinks && (
          <Box sx={{ textAlign: 'center', mt: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              variant="text"
              onClick={onSwitchToForgotPassword}
              disabled={loading}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '13px' }}
            >
              Forgot password?
            </Button>
            <Button
              variant="text"
              onClick={onSwitchToSignUp}
              disabled={loading}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '13px' }}
            >
              Create an account
            </Button>
          </Box>
        )}

    </div>

    {/* Secret tap zone — bottom-right corner, invisible, toggles "Remember this device" */}
    <div
      onClick={() => setShowRemember(v => !v)}
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 80,
        height: 80,
        zIndex: 9999,
        cursor: 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    />
    </>
  )};